import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { observer } from 'mobx-react-lite';
import { Field } from 'shared/ui/Field';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { TextMedium } from 'shared/ui/Typography';
import { colors } from 'shared/styles/colors';
import { userStore } from 'entities/user/model/user-store';
import { useNavigate } from 'react-router-dom';
import { modalStore } from 'entities/modal/model/modal.store';
import { getApiUrl } from 'shared/api/config';

const FormWrapper = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media (max-height: 900px) { gap: 18px; }
  @media (max-height: 800px) { gap: 14px; }
`;
const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const RegisterRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  gap: 8px;
`;
const RegisterLink = styled.a`
  color: ${colors.accentSecondary};
  text-decoration: none;
  font-size: 16px;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;
const ErrorMessage = styled.div`
  color: ${colors.errorState};
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 4px;
`;
const SocialLoginContainer = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
`;
const SocialButton = styled.button`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: ${colors.accentQuaternary}; }
`;
/* bottom links at the very bottom of the form */
const BottomLinks = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
  a { font-size: 14px; }
`;

const registerSchema = z.object({
  first_name: z.string()
    .min(1, 'Ім\'я повинно містити мінімум 1 символ')
    .max(60, 'Ім\'я повинно містити максимум 60 символів')
    .refine(v => !/\d/.test(v), { message: 'Ім\'я не повинно містити цифри' }),
  last_name: z.string()
    .min(1, 'Прізвище повинно містити мінімум 1 символ')
    .max(60, 'Прізвище повинно містити максимум 60 символів')
    .refine(v => !/\d/.test(v), { message: 'Прізвище не повинно містити цифри' }),
  email: z.string().email('Невірний формат email'),
  phone_number: z.string().min(1, 'Телефон є обовʼязковим полем').max(20, 'Телефон повинен містити максимум 20 символів'),
  password: z.string()
    .min(8, 'Пароль повинен містити мінімум 8 символів')
    .max(64, 'Пароль повинен містити максимум 64 символи')
    .refine(v => /[A-Z]/.test(v), { message: 'Пароль повинен містити хоча б одну велику літеру' })
    .refine(v => /\d/.test(v), { message: 'Пароль повинен містити хоча б одну цифру' }),
  confirmPassword: z.string(),

  /* required agreements */
  offerAgreement: z.boolean().refine(v => v === true, { message: 'Необхідно прийняти договір оферти' }),
  privacyPolicy: z.boolean().refine(v => v === true, { message: 'Необхідно прийняти політику конфіденційності' }),
  termsOfService: z.boolean().refine(v => v === true, { message: 'Необхідно прийняти відмову від відповідальності' }),

  /* optional */
  newsletter: z.boolean().optional(),
});
type RegisterFormData = z.infer<typeof registerSchema>;

export const RegisterForm = observer(() => {
  const navigate = useNavigate();

  const handlePrivacyPolicyClick = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    modalStore.openModal('privacyPolicy');
  };
  const handleTermsOfServiceClick = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    modalStore.openModal('termsOfService');
  };
  const handleOfferClick = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    modalStore.openModal('offer'); // modal should render OfferPage content
  };
  const handleNewsletterClick = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    modalStore.openModal('newsletter');
  };

  const methods = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      password: '',
      confirmPassword: '',
      offerAgreement: true,
      privacyPolicy: true,
      termsOfService: true,
      newsletter: true,
    },
    mode: 'onChange',
  });

  const { handleSubmit, watch, setValue, formState, setError } = methods;
  const offerAgreement = watch('offerAgreement');
  const privacyPolicy = watch('privacyPolicy');
  const termsOfService = watch('termsOfService');
  const newsletter = watch('newsletter');

  useEffect(() => {
    return () => { userStore.resetAuthErrors?.(); };
  }, []);

  const onSubmit = handleSubmit(async (data: RegisterFormData) => {
    try {
      userStore.resetAuthErrors?.();
      const requestData = {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        password: data.password,
        phone_number: data.phone_number,
        offer_agreement: data.offerAgreement,
      };
      const success = await userStore.register(requestData);
      if (success) navigate('/');
      else if (userStore.authError) {
        setError('root', { type: 'manual', message: userStore.authError });
      }
    } catch (error) {
      setError('root', { type: 'manual', message: 'Помилка при реєстрації. Спробуйте пізніше.' });
    }
  });

  useEffect(() => {
    const subscription = methods.watch(() => {
      if (formState.errors.root) methods.clearErrors('root');
    });
    return () => subscription.unsubscribe();
  }, [methods, formState.errors.root]);

  const handleGoogleLogin = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/oauth-callback`);
    window.location.href = `${getApiUrl()}/accounts/google/login/?redirect_uri=${redirectUri}`;
  };

  return (
    <FormProvider {...methods}>
      <FormWrapper onSubmit={onSubmit}>
        <Field
          name="first_name"
          type="text"
          placeholder="Іʼмя *"
          error={formState.errors.first_name?.message}
          isSuccessful={!formState.errors.first_name && formState.dirtyFields.first_name}
          onChange={(e) => setValue('first_name', e.target.value)}
        />
        {formState.errors.first_name && <ErrorMessage>{formState.errors.first_name.message}</ErrorMessage>}

        <Field
          name="last_name"
          type="text"
          placeholder="Прізвище *"
          error={formState.errors.last_name?.message}
          isSuccessful={!formState.errors.last_name && formState.dirtyFields.last_name}
          onChange={(e) => setValue('last_name', e.target.value)}
        />
        {formState.errors.last_name && <ErrorMessage>{formState.errors.last_name.message}</ErrorMessage>}

        <Field
          name="phone_number"
          type="text"
          placeholder="Телефон*"
          error={formState.errors.phone_number?.message}
          isSuccessful={!formState.errors.phone_number && formState.dirtyFields.phone_number}
          onChange={(e) => setValue('phone_number', e.target.value)}
        />
        {formState.errors.phone_number && <ErrorMessage>{formState.errors.phone_number.message}</ErrorMessage>}

        <Field
          name="email"
          type="email"
          placeholder="E-mail *"
          error={formState.errors.email?.message}
          isSuccessful={!formState.errors.email && formState.dirtyFields.email}
          onChange={(e) => setValue('email', e.target.value)}
        />
        {formState.errors.email && <ErrorMessage>{formState.errors.email.message}</ErrorMessage>}

        <Field
          name="password"
          type="password"
          placeholder="Пароль *"
          error={formState.errors.password?.message}
          isSuccessful={!formState.errors.password && formState.dirtyFields.password}
          onChange={(e) => setValue('password', e.target.value)}
        />
        {formState.errors.password && <ErrorMessage>{formState.errors.password.message}</ErrorMessage>}

        <CheckboxGroup>
          {/* Offer (required) */}
          <div className="flex align-center gap-2">
            <Checkbox
              checked={offerAgreement}
              onChange={(checked) => setValue('offerAgreement', checked)}
            />
            <TextMedium>
              Згоден з{' '}
              <RegisterLink className="p-0 underline" onClick={handleOfferClick}>
                Договором оферти
              </RegisterLink>
            </TextMedium>
          </div>
          {formState.errors.offerAgreement && (
            <ErrorMessage>{formState.errors.offerAgreement.message}</ErrorMessage>
          )}

          {/* Privacy (required) */}
          <div className="flex align-center gap-2">
            <Checkbox
              checked={privacyPolicy}
              onChange={(checked) => setValue('privacyPolicy', checked)}
            />
            <TextMedium>
              Згоден з{' '}
              <RegisterLink className="p-0 underline" onClick={handlePrivacyPolicyClick}>
                Політикою конфіденційності
              </RegisterLink>
            </TextMedium>
          </div>
          {formState.errors.privacyPolicy && (
            <ErrorMessage>{formState.errors.privacyPolicy.message}</ErrorMessage>
          )}

          {/* Liability waiver (required) */}
          <div className="flex align-center gap-2">
            <Checkbox
              checked={termsOfService}
              onChange={(checked) => setValue('termsOfService', checked)}
            />
            <TextMedium>
              Згоден з{' '}
              <RegisterLink className="p-0 underline" onClick={handleTermsOfServiceClick}>
                Відмовою від відповідальності
              </RegisterLink>
            </TextMedium>
          </div>
          {formState.errors.termsOfService && (
            <ErrorMessage>{formState.errors.termsOfService.message}</ErrorMessage>
          )}

          {/* Newsletter (optional) */}
          <div className="flex align-center gap-2">
            <Checkbox
              checked={!!newsletter}
              onChange={(checked) => setValue('newsletter', checked)}
            />
            <TextMedium>
              Згоден з{' '}
              <RegisterLink className="p-0 underline" onClick={handleNewsletterClick}>
                Розсилкою
              </RegisterLink>
            </TextMedium>
          </div>
        </CheckboxGroup>

        {formState.errors.root && <ErrorMessage>{formState.errors.root.message}</ErrorMessage>}

        <div className="flex-col">
          <Button type="submit" variant="primary" fullWidth>
            Зареєструватися
          </Button>
          <RegisterRow>
            <TextMedium>Вже є обліковий запис?</TextMedium>
            <RegisterLink href="/login">Увійти</RegisterLink>
          </RegisterRow>
        </div>

        <SocialLoginContainer>
          <SocialButton type="button" onClick={handleGoogleLogin}>
            {/* Google icon */}
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="60" height="60" rx="30" fill="#E9E9E9"/>
              <g clipPath="url(#clip0_3636_362)">
                <path d="M21.5 30.0002C21.5 28.4148 21.9368 26.9296 22.6957 25.6579V20.2637H17.3014C15.1605 23.0442 14 26.4329 14 30.0002C14 33.5676 15.1605 36.9563 17.3014 39.7368H22.6957V34.3425C21.9368 33.0709 21.5 31.5857 21.5 30.0002Z" fill="#FBBD00"/>
                <path d="M30 38.5005L26.25 42.2505L30 46.0005C33.5674 46.0005 36.9561 44.84 39.7366 42.699V37.3105H34.3481C33.0652 38.0722 31.5738 38.5005 30 38.5005Z" fill="#0F9D58"/>
                <path d="M22.695 34.3418L17.3008 39.736C17.7247 40.2865 18.1862 40.8137 18.6857 41.3132C21.7077 44.3352 25.7256 45.9995 29.9993 45.9995V38.4995C26.8978 38.4995 24.1795 36.8295 22.695 34.3418Z" fill="#31AA52"/>
                <path d="M46 29.9993C46 29.0258 45.9119 28.0506 45.738 27.1007L45.5973 26.332H30V33.832H37.5907C36.8536 35.2983 35.7202 36.4947 34.348 37.3094L39.7365 42.6979C40.287 42.274 40.8142 41.8125 41.3137 41.313C44.3357 38.291 46 34.273 46 29.9993Z" fill="#3C79E6"/>
                <path d="M36.0104 23.9896L36.6733 24.6524L41.9766 19.3492L41.3137 18.6863C38.2917 15.6643 34.2738 14 30 14L26.25 17.75L30 21.5C32.2704 21.5 34.4049 22.3841 36.0104 23.9896Z" fill="#CF2D48"/>
                <path d="M29.9994 21.5V14C25.7257 14 21.7077 15.6643 18.6857 18.6863C18.1862 19.1857 17.7247 19.7129 17.3008 20.2634L22.695 25.6577C24.1796 23.17 26.8979 21.5 29.9994 21.5Z" fill="#EB4132"/>
              </g>
              <defs><clipPath id="clip0_3636_362"><rect width="32" height="32" fill="white" transform="translate(14 14)"/></clipPath></defs>
            </svg>
          </SocialButton>
        </SocialLoginContainer>

        {/* bottom links row, offer at the very end */}
        <BottomLinks>
          <RegisterLink onClick={handleTermsOfServiceClick}>Відмова від відповідальності</RegisterLink>
          <RegisterLink onClick={handlePrivacyPolicyClick}>Політика конфіденційності</RegisterLink>
          <RegisterLink onClick={handleOfferClick}>Договір оферти</RegisterLink>
        </BottomLinks>
      </FormWrapper>
    </FormProvider>
  );
});
